/**
 * Authentication Storage Module
 * Handles secure storage and retrieval of authentication data
 * 
 * Uses single-encryption pattern: callers pass raw values, which are encrypted
 * once for storage and decrypted once on retrieval. This is functionally
 * equivalent to the legacy double-encryption (where the caller pre-encrypted
 * and the setter encrypted again); both yield the raw value from the getters.
 * Encryption key sourced from environment variable for security.
 */

import { AuthType } from '../api/types';
import { createLogger } from '../utils/logging';

const CryptoJs = require('crypto-js');
const logger = createLogger('AuthStorage');

const SECRET_KEY = process.env.REACT_APP_API_AES_PASS;

// ============================================================================
// Encryption/Decryption Utilities
// ============================================================================

/**
 * Encrypt a string value
 */
export function encrypt(value: string): string {
  return CryptoJs.AES.encrypt(value, SECRET_KEY).toString();
}

/**
 * Decrypt a string value
 */
export function decrypt(encryptedValue: string, options: { silent?: boolean } = {}): string {
  if (!encryptedValue) return '';
  
  try {
    const bytes = CryptoJs.AES.decrypt(encryptedValue, SECRET_KEY);
    return bytes.toString(CryptoJs.enc.Utf8);
  } catch (error) {
    if (!options.silent) {
      logger.error('Decryption Failed', 'Unable to decrypt stored value', { error });
    }
    return '';
  }
}

function decryptStoredValue(storedValue: string | null): string {
  if (!storedValue) return '';

  const firstPass = decrypt(storedValue);
  if (!firstPass) return '';

  // Older NextGen builds persisted values that were effectively double-encrypted.
  // A second decrypt only succeeds for that legacy format; otherwise it returns '' —
  // expected whenever firstPass (plaintext) happens to look like base64 ciphertext,
  // so don't log noise for this routine, always-attempted fallback.
  const secondPass = decrypt(firstPass, { silent: true });
  return secondPass || firstPass;
}

// ============================================================================
// Session ID Management
// ============================================================================

/**
 * Store session ID (encrypted)
 */
export function setSessionId(sessionId: string): void {
  const encrypted = encrypt(sessionId);
  sessionStorage.setItem('sessionId', encrypted);
}

/**
 * Retrieve session ID (decrypted)
 */
export function getSessionId(): string {
  return decryptStoredValue(sessionStorage.getItem('sessionId'));
}

/**
 * Clear session ID
 */
export function clearSessionId(): void {
  sessionStorage.removeItem('sessionId');
}

// ============================================================================
// Auth Type Management
// ============================================================================

/**
 * Store authentication type (encrypted)
 */
export function setAuthType(authType: AuthType | string): void {
  const encrypted = encrypt(authType);
  sessionStorage.setItem('authType', encrypted);
}

/**
 * Retrieve authentication type (decrypted)
 */
export function getAuthType(): string {
  return decryptStoredValue(sessionStorage.getItem('authType'));
}

/**
 * Clear authentication type
 */
export function clearAuthType(): void {
  sessionStorage.removeItem('authType');
}

// ============================================================================
// Complete Session Management
// ============================================================================

/**
 * Clear all authentication data
 */
export function clearSession(): void {
  clearSessionId();
  clearAuthType();
}

/**
 * Check if session exists
 */
export function hasSession(): boolean {
  return !!getSessionId();
}

/**
 * Check if auth type is set
 */
export function hasAuthType(): boolean {
  return !!getAuthType();
}
